"use client";

import { useEffect } from "react";

import { getTabSessionId } from "@repo/ui/lib/tabSessionId";

const REDUX_PREFIX = "persist:redux:"; // Actual format in localStorage
const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes (10 min session + 5 min buffer)
const PING_CHANNEL = "tab-ping";

/**
 * Component that cleans up orphaned Redux data on mount.
 *
 * Orphaned data =  Redux data from tabs that are no longer open.
 * This handles crash scenarios where beforeunload doesn't fire.
 *
 * Strategy:
 * 1. Broadcast a "ping" message asking all tabs to respond with their session IDs
 * 2. Wait for responses (100ms)
 * 3. Remove Redux data for session IDs that didn't respond AND are older than 15 min
 */
export function OrphanedReduxCleaner() {
  useEffect(() => {
    const run = async () => {
      try {
        // Get THIS tab's session ID
        const currentSessionId = getTabSessionId();
        if (!currentSessionId) {
          return;
        }

        const activeTabs = new Set<string>([currentSessionId]); // Current tab is always active

        // Create broadcast channel to detect other tabs
        const channel = new BroadcastChannel(PING_CHANNEL);

        // Listen for responses from other tabs
        const responseHandler = (event: MessageEvent) => {
          if (event.data.type === "pong" && event.data.sessionId) {
            activeTabs.add(event.data.sessionId);
          }
        };

        channel.addEventListener("message", responseHandler);

        // Broadcast ping to all tabs
        channel.postMessage({ type: "ping" });

        // Wait for responses (100ms should be enough for local communication)
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Stop listening
        channel.removeEventListener("message", responseHandler);
        channel.close();

        // Now check localStorage and remove orphaned data
        const now = Date.now();
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key?.startsWith(REDUX_PREFIX)) {
            continue;
          }

          // Extract session ID from key
          const sessionId = key.replace(REDUX_PREFIX, "");

          // Check if this session is from an active tab
          if (activeTabs.has(sessionId)) {
            continue; // Skip - tab is still open
          }

          // Tab is not active - check if data is old enough to remove
          try {
            const value = localStorage.getItem(key);
            if (!value) {
              continue;
            }

            const data = JSON.parse(value);
            const lastSaved = data._timestamp;

            // Remove if no timestamp OR older than 15 minutes
            if (!lastSaved || now - lastSaved > SESSION_TIMEOUT) {
              keysToRemove.push(key);
            }
          } catch {
            // Corrupted data - remove it (it's not from an active tab)
            keysToRemove.push(key);
          }
        }

        // Remove orphaned keys
        if (keysToRemove.length > 0) {
          keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
          });
        }
      } catch (error) {
        console.warn("[OrphanedReduxCleaner] Failed to clean up orphaned data:", error);
      }
    };

    run();
  }, []); // Run only once on mount

  return null; // This component doesn't render anything
}
